import type { ThemeOptions } from '@mui/material/styles';
import { CONTENT_FONT_FAMILY_VAR } from './fonts';

/**
 * Content-area type scale (Readex Pro). Hierarchy:
 * page title (h4) → section title (h6, title case in English) → description
 * (body2) → figure (`CONTENT_FIGURE`, bold) → label (overline, medium) →
 * metadata (caption).
 *
 * Labels are normal case with no tracking in every language (no all-caps;
 * tracking breaks Arabic joining). `latin` only gates the negative tracking on
 * large headings.
 */
export function createContentTypography(latin: boolean): ThemeOptions['typography'] {
  return {
    fontFamily: CONTENT_FONT_FAMILY_VAR,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    h1: { fontSize: '2rem', fontWeight: 700, lineHeight: 1.2, letterSpacing: latin ? '-0.015em' : 0 },
    h2: { fontSize: '1.75rem', fontWeight: 700, lineHeight: 1.2, letterSpacing: latin ? '-0.01em' : 0 },
    h3: { fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.25, letterSpacing: latin ? '-0.01em' : 0 },
    h4: { fontSize: '1.625rem', fontWeight: 700, lineHeight: 1.25, letterSpacing: latin ? '-0.01em' : 0 },
    h5: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.35 },
    h6: { fontSize: '1.0625rem', fontWeight: 600, lineHeight: 1.4 },
    subtitle1: { fontSize: '1rem', fontWeight: 500, lineHeight: 1.5 },
    subtitle2: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.45 },
    body1: { fontSize: '0.9375rem', lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', lineHeight: 1.55 },
    caption: { fontSize: '0.75rem', lineHeight: 1.45 },
    overline: {
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.45,
      letterSpacing: 0,
      textTransform: 'none',
    },
    button: { fontSize: '0.875rem', fontWeight: 600, textTransform: 'none', letterSpacing: 0 },
  };
}

/**
 * Editorial figure sizes for operational numbers. Combine with
 * `CONTENT_FIGURE_BASE`; the number carries the visual weight, not an icon or
 * a coloured card.
 */
export const CONTENT_FIGURE = {
  hero: { fontSize: { xs: '2.25rem', md: '3rem' }, lineHeight: 1.05 },
  lg: { fontSize: { xs: '1.75rem', md: '2.125rem' }, lineHeight: 1.1 },
  md: { fontSize: '1.5rem', lineHeight: 1.15 },
  sm: { fontSize: '1.25rem', lineHeight: 1.2 },
} as const;

export type ContentFigureSize = keyof typeof CONTENT_FIGURE;

export const CONTENT_FIGURE_BASE = {
  fontWeight: 700,
  fontVariantNumeric: 'tabular-nums lining-nums',
  fontFeatureSettings: '"tnum" 1, "lnum" 1',
} as const;
